import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { STORAGE_KEYS } from "@/constants/storage";
import { callApi } from "../callEndpoint";
import {
  authEndpoints,
  type PasswordlessVerifyResponse,
  type SignupRequestDto,
  type SignupResponse,
} from "../contracts/auth.contract";
import { decodeJwt, USER_QUERY_KEY } from "./user.hooks";

export const useSignup = () =>
  useMutation({
    mutationFn: (data: SignupRequestDto) =>
      callApi<SignupResponse>(authEndpoints.signup, { data }),
  });

export const usePasswordlessRequest = () =>
  useMutation({
    mutationFn: (data: { email: string }) =>
      callApi(authEndpoints.requestPasswordless, { data }),
  });

export const usePasswordlessVerify = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; code: string }) =>
      callApi<PasswordlessVerifyResponse>(authEndpoints.verifyPasswordless, {
        data,
      }),
    onSuccess: (result) => {
      sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, result.accessToken);
      const user = decodeJwt(result.accessToken);
      queryClient.setQueryData(USER_QUERY_KEY, user);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => callApi(authEndpoints.logout),
    onSettled: () => {
      sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      queryClient.clear();
      navigate({ to: "/login" });
    },
  });
};
